import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';

/**
 * Checks if a cell value represents zero or empty.
 * Handles: null, undefined, '---', '', and numeric 0.
 * String values (labels) are not considered zero/empty.
 * @param cell - The cell object containing kpiValue
 * @returns true if value is zero or empty, false otherwise
 */
export const isValueZeroOrEmpty = (cell: any): boolean => {
  if (cell === null || cell === undefined) return true;

  const val = cell?.kpiValue;
  if (val === null || val === undefined || val === '---' || val === '') return true;

  // Handle numeric zero
  if (typeof val === 'number') return val === 0;

  // If it's a string, it's a label (like "PY 26") - not a value we check
  return false;
};

/**
 * Checks if all numeric cells in a row are zero or empty.
 * Skips non-numeric kpiValues (labels like "PY 26", "HAIR COLOR").
 * @param row - Array of KPI cells to check
 * @returns true if all numeric cells are zero/empty, false otherwise
 */
export const isRowAllZeros = (row: any[]): boolean => {
  if (!row || !Array.isArray(row) || row.length <= 1) return true;

  for (let i = 0; i < row.length; i++) {
    // Skip non-numeric kpiValues (labels)
    if (typeof row[i].kpiValue !== 'number') continue;
    if (!isValueZeroOrEmpty(row[i])) {
      return false;
    }
  }
  return true;
};

/**
 * Checks if a parent row with children should be filtered out.
 * @param row - Array of KPI cells, where first cell may contain rowComponent
 * @returns true if all children have all zeros (parent should be removed), false otherwise
 */
export const shouldFilterParentRow = (row: any[]): boolean => {
  if (!row || !Array.isArray(row) || row.length === 0) return true;

  const firstCell = row[0];
  const rowComponent = firstCell?.rowComponent;

  // If no rowComponent, check the row itself
  if (!rowComponent || !Array.isArray(rowComponent) || rowComponent.length === 0) {
    return isRowAllZeros(row);
  }

  // Check if ALL children (rowComponent) have all zeros
  for (const child of rowComponent) {
    if (!isRowAllZeros(child)) {
      return false; // At least one child has non-zero values, keep the parent
    }
  }

  return true; // All children have all zeros, filter out the parent
};

/**
 * Filters out rows where all values are zero or empty from table data.
 * @param tableData - Table data object with body.rows
 * @returns New table data with zero rows filtered out
 */
export const filterZeroRows = (tableData: any): any => {
  if (!tableData || !tableData.body || !tableData.body.rows) return tableData;

  return {
    ...tableData,
    body: {
      ...tableData.body,
      rows: tableData.body.rows.filter((row: any) => !shouldFilterParentRow(row)),
    },
  };
};

// Transform table data to handle rowComponent structure (for monthly view)
// API format: { rows: [{ kpiValue: "Performance Bonus", rowComponent: [[...], [...], {...}, {...}] }] }
export const transformTableData = (tableData: any) => {
  if (!tableData || !tableData.body || !tableData.body.rows) return tableData;

  const transformedRows = tableData.body.rows
    .map((row: any) => {
      if (Array.isArray(row)) {
        if (row.length > 0 && row[0]?.rowComponent) {
          const newRow = [...row];
          newRow[0] = {
            ...newRow[0],
            children: newRow[0].rowComponent,
          };
          if (typeof newRow[0].kpiValue === 'string') {
            const originalValue = newRow[0].kpiValue;
            newRow[0].kpiValue = startCase(toLower(originalValue));
          }
          return newRow;
        }
        return row;
      }

      if (row && typeof row === 'object' && row.rowComponent && Array.isArray(row.rowComponent)) {
        const rowComponent = row.rowComponent;
        const children: any[] = [];
        const mainRowCells: any[] = [];

        rowComponent.forEach((item: any) => {
          if (Array.isArray(item)) {
            const filteredChildRow = item.filter(
              (cell: any) => cell !== undefined && cell !== null
            );
            if (filteredChildRow.length > 0) {
              children.push(filteredChildRow);
            }
          } else if (item && typeof item === 'object' && item !== null) {
            mainRowCells.push(item);
          }
        });

        // Create the first cell with children if there are any
        const firstCell: any = {
          kpiValue: row.kpiValue,
          kpiDataType: row.kpiDataType,
          kpiCurrency: row.kpiCurrency,
        };

        if (children.length > 0) {
          firstCell.children = children;
        }

        return [firstCell, ...mainRowCells];
      }

      if (row && typeof row === 'object' && !Array.isArray(row)) {
        return [row];
      }

      return row;
    })
    .filter((row: any) => {
      if (!row) return false;
      if (Array.isArray(row)) {
        return row.length > 0 && row[0] !== undefined && row[0] !== null;
      }
      return true;
    });

  return {
    ...tableData,
    body: {
      ...tableData.body,
      rows: transformedRows,
    },
  };
};

// Sanitize table data to filter out undefined/null rows and ensure row[0] exists
export const sanitizeTableData = (tableData: any) => {
  const transformed = transformTableData(tableData);
  if (!transformed || !transformed.body || !transformed.body.rows) return transformed;

  return {
    ...transformed,
    body: {
      ...transformed.body,
      rows: transformed.body.rows
        .filter(
          (row: any) =>
            row && Array.isArray(row) && row.length > 0 && row[0] !== undefined && row[0] !== null
        )
        .map((row: any) =>
          row.map((cell: any) => {
            if (cell && typeof cell === 'object') {
              return cell;
            }
            if (cell !== undefined && cell !== null) {
              return { kpiValue: cell, kpiDataType: 'string' };
            }
            return { kpiValue: null, kpiDataType: 'string' };
          })
        ),
    },
  };
};
